import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlusIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon, TrashIcon, SparklesIcon, PencilIcon, AtSymbolIcon, ArrowUpTrayIcon, ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PhoneInput } from '../components/ui/PhoneInput';
import LockedField from '../components/ui/LockedField';

// Example tool templates
const TOOL_EXAMPLES = {
  get_weather: {
    name: "get_weather",
    description: "Determine weather in my location",
    strict: true,
    parameters: {
      type: "object",
      properties: {
        location: {
          type: "string",
          description: "The city and state e.g. San Francisco, CA"
        },
        unit: {
          type: "string",
          enum: ["c", "f"]
        }
      },
      additionalProperties: false,
      required: ["location", "unit"]
    }
  },
  send_notification: {
    name: "send_notification",
    description: "Send a notification to the user",
    strict: true,
    parameters: {
      type: "object",
      properties: {
        message: {
          type: "string",
          description: "The notification message to send"
        },
        priority: {
          type: "string",
          enum: ["low", "normal", "high"],
          description: "Priority level of the notification"
        }
      },
      additionalProperties: false,
      required: ["message"]
    }
  },
  lookup_customer: {
    name: "lookup_customer",
    description: "Look up customer information by ID or email",
    strict: true,
    parameters: {
      type: "object",
      properties: {
        customer_id: {
          type: "string",
          description: "The unique customer identifier"
        },
        email: {
          type: "string",
          description: "Customer email address"
        }
      },
      additionalProperties: false,
      required: []
    }
  }
};

const DEFAULT_TOOL_SCHEMA = {
  name: "my_function",
  description: "Describe what this function does",
  strict: true,
  parameters: {
    type: "object",
    properties: {
      param1: {
        type: "string",
        description: "Description of parameter"
      }
    },
    additionalProperties: false,
    required: ["param1"]
  }
};

const REGION_OPTIONS = [
  { value: 'none', label: 'Sin región específica' },
  { value: 'tabasco', label: 'Tabasco' },
  { value: 'yucatan', label: 'Yucatán' },
];

const normalizeRegion = (region) => (
  ['none', 'tabasco', 'yucatan'].includes(region) ? region : 'none'
);

/** Conditional LockedField wrapper — keeps JSX DRY */
const MaybeLocked = ({ locked, children }) =>
  locked ? <LockedField>{children}</LockedField> : <>{children}</>;

/** E.164 phone validation — defined at module scope so it is never re-created per render
 *  and can be imported/tested independently without mounting the full component. */
const isValidE164 = (phone) => /^\+[1-9]\d{6,14}$/.test((phone || '').trim());

function AgentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isEditing = Boolean(id);
  const [searchParams] = useSearchParams();
  const isChildMode = !isEditing && searchParams.get('mode') === 'child';
  const parentIdFromUrl = searchParams.get('parent') || '';
  const [parentAgent, setParentAgent] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbUploading, setKbUploading] = useState(false);
  const [knowledgeBaseDocuments, setKnowledgeBaseDocuments] = useState([]);
  const [knowledgeBaseTotalSize, setKnowledgeBaseTotalSize] = useState(0);
  // isChild moved here (after formData declaration) to avoid TDZ error

  // eslint-disable-next-line prefer-const
  const [formData, setFormData] = useState({
    name: '',
    instructions: '',
    inbound_instructions: '',
    voice: 'marin',
    voice_secondary: '',
    language: 'es-MX',
    tools: [],
    sensitivity_preset: 'responsive',
    vad_settings: {},
    tts_provider: 'elevenlabs',
    elevenlabs_voice_id: '',
    elevenlabs_voice_id_secondary: '',
    elevenlabs_model_id: 'eleven_flash_v2_5',
    elevenlabs_voice_settings: { stability: 0.5, similarity_boost: 0.75 },
    natural_fillers: false,
    knowledge_base: '',
    audio_filter_preset: 'coffee_shop',
    agent_type: 'general',
    department: 'ventas_cobranza',
    handoff_phone_number: '',
    handoff_primary_name: '',
    handoff_directory: [],
    handoff_hold_music_url: '',
    handoff_ring_timeout_seconds: 30,
    handoff_no_answer_message: '',
    region: 'none',
    whatsapp_enabled: false,
    sms_enabled: false,
    sms_from: '',
    composio_integration: {
      enabled: false,
      connected_account_id: '',
      toolkit: 'GOOGLECALENDAR',
    },
    // Handoff v2 — employee directory
    handoff_directory: [],
    handoff_hold_music_url: '',
    handoff_ring_timeout_seconds: 15,
    handoff_no_answer_message: '',
    // Agent ownership & hierarchy
    owner_user_id: '',
    handoff_primary_phone: '',
    parent_agent_id: '',
    // Salesperson ownership (legacy, kept for backward compat)
    owner_salesperson_id: '',
  });
  const [allAgents, setAllAgents] = useState([]);       // for parent agent dropdown
  const isChild = isEditing && !!formData.parent_agent_id;
  const isParent = isEditing && !!id && allAgents.some(a => a.parent_agent_id === id);
  const [creationMode, setCreationMode] = useState('scratch'); // 'scratch' | 'based_on'
  const [teamUsers, setTeamUsers] = useState([]);       // for owner dropdown
  const [salespersonProfiles, setSalespersonProfiles] = useState([]);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [composioConnecting, setComposioConnecting] = useState(false);
  const [composioStatus, setComposioStatus] = useState(null); // null=loading, {connected,email}
  const [composioStatusLoading, setComposioStatusLoading] = useState(false);
  const [composioDisconnecting, setComposioDisconnecting] = useState(false);
  const [handoffDirSaving, setHandoffDirSaving] = useState(false);
  const [elevenlabsVoices, setElevenlabsVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [availableTools, setAvailableTools] = useState({}); // Categories with tools

  // Allowed organizations (super_admin only)
  const [tenants, setTenants] = useState([]);
  const [allowedTenants, setAllowedTenants] = useState([]);

  // Tool creation modal state
  const [showToolModal, setShowToolModal] = useState(false);
  const [toolJsonText, setToolJsonText] = useState(JSON.stringify(DEFAULT_TOOL_SCHEMA, null, 2));
  const [toolJsonError, setToolJsonError] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [showExamplesDropdown, setShowExamplesDropdown] = useState(false);
  const [creatingTool, setCreatingTool] = useState(false);
  const [editingToolId, setEditingToolId] = useState(null); // null = creating, string = editing

  // AI Tool Generator state
  const [showGeneratorInput, setShowGeneratorInput] = useState(false);
  const [generatorPrompt, setGeneratorPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Dynamic Prompt variables state
  const [promptVariables, setPromptVariables] = useState({ system: [], custom: [] });
  const [showVarDropdown, setShowVarDropdown] = useState(false);
  const [varFilter, setVarFilter] = useState('');
  const [varDropdownPos, setVarDropdownPos] = useState({ top: 0, left: 0 });
  const instructionsRef = useRef(null);
  const kbUploadInputRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      fetchAgent();
      fetchComposioStatus(id, true); // force refresh on open to bust stale empty-email cache
    }
    fetchAvailableTools();
    fetchPromptVariables();
    fetchTenantInfo();
    // Fetch agents list (for based_on parent dropdown) and team users (for owner dropdown)
    api.get('/agents').then(r => setAllAgents(r.data || [])).catch(() => {});

    // Child mode: fetch parent agent and prefill inherited fields
    if (isChildMode && parentIdFromUrl) {
      api.get(`/agents/${parentIdFromUrl}`)
        .then(r => {
          const p = r.data;
          setParentAgent(p);
          setFormData(prev => ({
            ...prev,
            parent_agent_id: p.id,
            instructions: p.instructions || prev.instructions,
            inbound_instructions: p.inbound_instructions || prev.inbound_instructions,
            voice: p.voice || prev.voice,
            voice_secondary: p.voice_secondary || prev.voice_secondary,
            language: p.language || prev.language,
            tools: p.tools || prev.tools,
            tts_provider: p.tts_provider || prev.tts_provider,
            elevenlabs_voice_id: p.elevenlabs_voice_id || prev.elevenlabs_voice_id,
            elevenlabs_voice_id_secondary: p.elevenlabs_voice_id_secondary || prev.elevenlabs_voice_id_secondary,
            agent_type: p.agent_type || prev.agent_type,
            department: p.department || prev.department,
            knowledge_base: p.knowledge_base || prev.knowledge_base,
            audio_filter_preset: p.audio_filter_preset || prev.audio_filter_preset,
            natural_fillers: typeof p.natural_fillers === 'boolean' ? p.natural_fillers : prev.natural_fillers,
            sensitivity_preset: p.sensitivity_preset || prev.sensitivity_preset,
          }));
          if (Array.isArray(p.knowledge_base_documents)) {
            setKnowledgeBaseDocuments(p.knowledge_base_documents);
            setKnowledgeBaseTotalSize(p.knowledge_base_total_size ?? 0);
          }
        })
        .catch(() => {});
    }
    api.get('/team/users')
      .then(r => setTeamUsers(r.data || []))
      .catch(err => {
        // /team/users is role-gated; non-admin users may get 403.
        // Leave teamUsers empty — the ensure-current-user effect below will inject the fallback.
        console.warn('AgentEditor: /team/users unavailable (status %s), will fall back to current-user entry', err?.response?.status);
        setTeamUsers([]);
        toast('No se pudo cargar la información del equipo, puede continuar con valores por defecto', { icon: '⚠️' });
      });
    // Legacy: salesperson profiles for backward-compat owner dropdown
    api.get('/salesperson-profiles').then(r => setSalespersonProfiles(r.data || [])).catch(() => {});
    if (isSuperAdmin) {
      api.get('/tenants').then(r => setTenants(r.data)).catch(() => {});
    }
  }, [id]);

  const fetchTenantInfo = async () => {
    try {
      const response = await api.get('/tenants/me');
      setTenantInfo(response.data || null);
    } catch (error) {
      console.error('Error fetching tenant info:', error);
      toast('No se pudo cargar la información del equipo, puede continuar con valores por defecto', { icon: '⚠️' });
    }
  };

  const fetchComposioStatus = async (agentId, forceRefresh = false) => {
    if (!agentId) return;
    setComposioStatusLoading(true);
    try {
      const url = forceRefresh
        ? `/agents/${agentId}/composio/status?refresh=true`
        : `/agents/${agentId}/composio/status`;
      const response = await api.get(url);
      setComposioStatus(response.data || { connected: false });
    } catch (error) {
      console.error('Error fetching Composio status:', error);
      setComposioStatus({ connected: false });
    } finally {
      setComposioStatusLoading(false);
    }
  };

  const handleComposioConnect = async () => {
    if (!id) {
      toast.error(t('agentEditor.composioSaveFirst'));
      return;
    }
    setComposioConnecting(true);
    try {
      const response = await api.post(`/agents/${id}/composio/connect`);
      const oauthUrl = response.data?.oauth_url;
      if (!oauthUrl) {
        toast.error(t('agentEditor.composioOAuthUrlMissing'));
        return;
      }
      const popup = window.open(oauthUrl, 'composio_oauth', 'width=600,height=700');
      if (!popup) {
        toast.error(t('agentEditor.composioPopupBlocked'));
        return;
      }
      const interval = setInterval(() => {
        if (popup.closed) {
          clearInterval(interval);
          // Refetch live status after OAuth popup closes
          fetchComposioStatus(id);
          fetchAgent();
        }
      }, 800);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      if (error.response?.status === 501) {
        toast.error(t('agentEditor.composioUnavailable'));
      } else {
        toast.error(t('agentEditor.composioOAuthError', { detail }));
      }
    } finally {
      setComposioConnecting(false);
    }
  };

  const handleComposioDisconnect = async () => {
    if (!id) return;
    setComposioDisconnecting(true);
    try {
      await api.delete(`/agents/${id}/composio/disconnect`);
      setComposioStatus({ connected: false });
      // Also clear local formData so the checkbox/state stays consistent
      setFormData(prev => ({
        ...prev,
        composio_integration: {
          ...(prev.composio_integration || {}),
          connected_account_id: '',
          email: '',
        },
      }));
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(t('agentEditor.composioDisconnectError', { detail }));
    } finally {
      setComposioDisconnecting(false);
    }
  };

  // Defensive sync: when Composio reports connected, mirror that into formData so the
  // 'Conectado' badge appears even before the user has saved the enabled=true DB flag.
  // Lens: defensive-robust — null-guards + debug logging for the failure path.
  useEffect(() => {
    if (composioStatus?.connected) {
      setFormData(prev => {
        if (prev.composio_integration?.enabled) return prev; // already in sync, no-op
        console.debug('[AgentEditor] composioStatus.connected=true but formData.enabled=false — syncing locally');
        return {
          ...prev,
          composio_integration: {
            ...(prev.composio_integration || { toolkit: 'GOOGLECALENDAR', connected_account_id: '' }),
            enabled: true,
          },
        };
      });
    }
  }, [composioStatus]);

  // Defensive sync: normalise sms_from / sms_enabled on id change so stale state from a
  // previous agent never bleeds through before fetchAgent resolves.
  useEffect(() => {
    setFormData(prev => {
      const needsNorm =
        typeof prev.sms_enabled !== 'boolean' ||
        typeof prev.sms_from !== 'string';
      if (!needsNorm) return prev; // already clean, no-op
      console.debug('[AgentEditor] sms fields non-canonical — normalising', { sms_enabled: prev.sms_enabled, sms_from: prev.sms_from });
      return {
        ...prev,
        sms_enabled: Boolean(prev.sms_enabled),
        sms_from: prev.sms_from || '',
      };
    });
  }, [id]);

  // Fetch ElevenLabs voices on mount (ElevenLabs is the only TTS provider)
  useEffect(() => {
    if (elevenlabsVoices.length === 0) {
      setLoadingVoices(true);
      api.get('/elevenlabs/voices')
        .then(res => {
          setElevenlabsVoices(res.data.voices || []);
        })
        .catch(err => {
          console.error('Failed to load ElevenLabs voices:', err);
          if (err.response?.status === 404) {
            toast.error(t('agentEditor.elevenlabsNotConfigured') || 'Voice service not configured on server');
          } else {
            toast.error(t('agentEditor.elevenlabsVoiceError') || 'Failed to load voices');
          }
        })
        .finally(() => setLoadingVoices(false));
    }
  }, []);

  // Sync live Composio connection status into formData so the UI
  // converges even when the DB enabled flag is still false.
  useEffect(() => {
    if (composioStatus?.connected) {
      setFormData(prev => ({
        ...prev,
        composio_integration: {
          ...(prev.composio_integration || { toolkit: 'GOOGLECALENDAR', connected_account_id: '' }),
          enabled: true,
        },
      }));
    }
  }, [composioStatus]);

  // Auto-default owner_user_id to current user when blank (new or existing agent)
  useEffect(() => {
    if (user?.user_id) {
      setFormData(prev => prev.owner_user_id ? prev : { ...prev, owner_user_id: user.user_id });
    }
  }, [isEditing, user?.user_id]);

  // Defensive guard: ensure the current user always has a matching <option> in the
  // owner dropdown regardless of role-gating on /api/team/users or async load order.
  // Without this, a 403 / empty teamUsers leaves the auto-defaulted UUID with no
  // matching option and the dropdown visually shows 'No owner (shared)'.
  useEffect(() => {
    if (!user?.user_id) return;
    setTeamUsers(prev => {
      const alreadyPresent = prev.some(u => u.user_id === user.user_id);
      if (alreadyPresent) return prev;
      // Inject synthetic entry for the current user so the selected value always renders.
      const syntheticEntry = {
        user_id: user.user_id,
        name: user.name || user.email || user.user_id,
        email: user.email || '',
        role: user.role || '',
      };
      console.log('AgentEditor: injecting current-user fallback into owner dropdown', syntheticEntry);
      return [syntheticEntry, ...prev];
    });
  }, [user?.user_id, user?.name, user?.email, user?.role]);

  const fetchKnowledgeBaseDocuments = async (agentId = id) => {
    if (!agentId) return;

    try {
      setKbLoading(true);
      const response = await api.get(`/agents/${agentId}/knowledge-base/documents`);
      setKnowledgeBaseDocuments(response.data.documents || []);
      setKnowledgeBaseTotalSize(response.data.total_size || 0);
      if (typeof response.data.knowledge_base === 'string') {
        setFormData(prev => ({ ...prev, knowledge_base: response.data.knowledge_base }));
      }
    } catch (error) {
      console.error('Error fetching knowledge base documents:', error);
      toast.error(t('agentEditor.failedFetchKnowledgeBaseDocuments', 'Failed to load knowledge base documents'));
    } finally {
      setKbLoading(false);
    }
  };

  const fetchAgent = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/agents/${id}`);
      setFormData(prev => ({
        ...prev,
        ...response.data,
        department: response.data?.department || 'ventas_cobranza',
        region: normalizeRegion(response.data?.region),
        whatsapp_enabled: Boolean(response.data?.whatsapp_enabled),
        sms_enabled: Boolean(response.data?.sms_enabled),
        sms_from: response.data?.sms_from || '',
        handoff_primary_name: response.data?.handoff_primary_name || '',
        handoff_hold_music_url: response.data?.handoff_hold_music_url || '',
        handoff_ring_timeout_seconds: response.data?.handoff_ring_timeout_seconds ?? 30,
        handoff_no_answer_message: response.data?.handoff_no_answer_message || '',
        // Mark every loaded directory entry as confirmed (persisted state)
        handoff_directory: (response.data?.handoff_directory || []).map(e => ({ ...e, _confirmed: true })),
        composio_integration: {
          enabled: Boolean(response.data?.composio_integration?.enabled),
          connected_account_id: response.data?.composio_integration?.connected_account_id || '',
          toolkit: response.data?.composio_integration?.toolkit || 'GOOGLECALENDAR',
        },
        handoff_directory: Array.isArray(response.data?.handoff_directory) ? response.data.handoff_directory : [],
        handoff_hold_music_url: response.data?.handoff_hold_music_url || '',
        // Agent ownership & hierarchy
        owner_user_id: response.data?.owner_user_id || '',
        handoff_primary_phone: response.data?.handoff_primary_phone || '',
        parent_agent_id: response.data?.parent_agent_id || '',
        // Salesperson ownership (legacy)
        owner_salesperson_id: response.data?.owner_salesperson_id || '',
      }));
      setAllowedTenants(response.data.allowed_tenants || []);
      setKnowledgeBaseDocuments(response.data.knowledge_base_documents || []);
      setKnowledgeBaseTotalSize(response.data.knowledge_base_total_size || 0);
      if (!response.data.knowledge_base_documents) {
        fetchKnowledgeBaseDocuments(id);
      }
    } catch (error) {
      toast.error(t('errors.failedFetchAgents'));
      console.error('Error fetching agent:', error);
    } finally {
      setLoading(false);
    }
  };

  // childPhoneMissing: true only when creating a child agent and the primary phone
  // has not yet been filled with a valid E.164 number.
  // MUST be declared AFTER formData useState to avoid a Temporal Dead Zone (TDZ) crash.
  const childPhoneMissing = isChildMode && !isValidE164(formData.handoff_primary_phone);

  const handleSaveHandoffSection = async () => {
    if (!isEditing) return;
    // Validate directory entries
    const dir = formData.handoff_directory || [];
    for (let i = 0; i < dir.length; i++) {
      const entry = dir[i];
      if (!entry.name || !entry.name.trim()) {
        toast.error(t('agentEditor.handoffRowNameEmpty', { row: i + 1 }));
        return;
      }
      if (!isValidE164(entry.phone)) {
        toast.error(t('agentEditor.handoffRowPhoneInvalid', { row: i + 1 }));
        return;
      }
    }
    try {
      setHandoffDirSaving(true);
      const patch = {
        handoff_primary_name: formData.handoff_primary_name || '',
        handoff_directory: dir.map(({ _confirmed, ...e }) => e),
        handoff_hold_music_url: formData.handoff_hold_music_url || '',
        handoff_ring_timeout_seconds: formData.handoff_ring_timeout_seconds ?? 30,
        handoff_no_answer_message: formData.handoff_no_answer_message || '',
      };
      await api.put(`/agents/${id}`, patch);
      // Mark all directory rows as confirmed after successful save
      setFormData(prev => ({
        ...prev,
        handoff_directory: (prev.handoff_directory || []).map(e => ({ ...e, _confirmed: true })),
      }));
      toast.success(t('agentEditor.handoffSaved'));
    } catch (err) {
      toast.error(t('agentEditor.handoffSaveError'));
      console.error('Error saving handoff section:', err);
    } finally {
      setHandoffDirSaving(false);
    }
  };

  const fetchAvailableTools = async () => {
    try {
      // When editing an agent, pass agent_id so backend includes owner's custom tools for shared agents
      const url = id ? `/tools?agent_id=${id}` : '/tools';
      const response = await api.get(url);
      // Backend returns categories: { general: [...], debt_collection: [...], ... }
      if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        setAvailableTools(response.data);
      } else {
        setAvailableTools({});
      }
    } catch (error) {
      console.error('Error fetching tools:', error);
      setAvailableTools({});
    }
  };

  const fetchPromptVariables = async () => {
    try {
      const response = await api.get('/prompt-variables');
      setPromptVariables(response.data || { system: [], custom: [] });
    } catch (error) {
      console.error('Error fetching prompt variables:', error);
    }
  };

  // All variables combined for autocomplete
  const allVariables = [...(promptVariables.system || []), ...(promptVariables.custom || [])];

  // Filter out alias variables for the chips panel (show only primary ones)
  const primarySystemVars = (promptVariables.system || []).filter(
    v => !['nombre', 'telefono', 'cuenta', 'objetivo'].includes(v.key)
  );

  const handleInstructionsChange = useCallback((e) => {
    const { value, selectionStart } = e.target;
    setFormData(prev => ({ ...prev, instructions: value }));

    // Check if user just typed @{ — show autocomplete
    const textBefore = value.substring(0, selectionStart);
    const atMatch = textBefore.match(/@\{(\w*)$/);

    if (atMatch) {
      setVarFilter(atMatch[1].toLowerCase());
      setShowVarDropdown(true);

      // Position dropdown near cursor
      const textarea = instructionsRef.current;
      if (textarea) {
        const rect = textarea.getBoundingClientRect();
        // Approximate position based on character count
        const lines = textBefore.split('\n');
        const lineHeight = 20;
        const charWidth = 7.5;
        const currentLine = lines.length - 1;
        const currentCol = lines[lines.length - 1].length;
        setVarDropdownPos({
          top: Math.min(currentLine * lineHeight + 30, rect.height - 50),
          left: Math.min(currentCol * charWidth, rect.width - 200),
        });
      }
    } else {
      setShowVarDropdown(false);
    }
  }, []);

  const insertVariable = useCallback((varKey) => {
    const textarea = instructionsRef.current;
    if (!textarea) return;

    const { value, selectionStart } = textarea;
    const textBefore = value.substring(0, selectionStart);
    const textAfter = value.substring(selectionStart);

    // Find the @{ prefix to replace
    const atMatch = textBefore.match(/@\{(\w*)$/);
    if (atMatch) {
      const prefixStart = textBefore.length - atMatch[0].length;
      const newValue = textBefore.substring(0, prefixStart) + `@{${varKey}}` + textAfter;
      setFormData(prev => ({ ...prev, instructions: newValue }));

      // Restore cursor position after the inserted variable
      const newCursorPos = prefixStart + varKey.length + 3; // @{ + key + }
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    } else {
      // No @{ prefix — just insert at cursor (from chip click)
      const newValue = textBefore + `@{${varKey}}` + textAfter;
      setFormData(prev => ({ ...prev, instructions: newValue }));

      const newCursorPos = selectionStart + varKey.length + 3;
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }

    setShowVarDropdown(false);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToolToggle = (toolName) => {
    setFormData(prev => ({
      ...prev,
      tools: prev.tools.includes(toolName)
        ? prev.tools.filter(tool => tool !== toolName)
        : [...prev.tools, toolName],
    }));
  };

  // Tool modal functions
  const openToolModal = () => {
    setEditingToolId(null);
    setToolJsonText(JSON.stringify(DEFAULT_TOOL_SCHEMA, null, 2));
    setToolJsonError(null);
    setWebhookUrl('');
    setShowGeneratorInput(false);
    setGeneratorPrompt('');
    setShowToolModal(true);
  };

  const openEditToolModal = (tool) => {
    setEditingToolId(tool.tool_id);
    // Build the schema from tool data
    const schema = {
      name: tool.name,
      description: tool.description,
      strict: true,
      parameters: tool.parameters || {
        type: "object",
        properties: {},
        required: [],
        additionalProperties: false
      }
    };
    setToolJsonText(JSON.stringify(schema, null, 2));
    setToolJsonError(null);
    setWebhookUrl(tool.webhook_url || '');
    setShowGeneratorInput(false);
    setGeneratorPrompt('');
    setShowToolModal(true);
  };

  const handleToolJsonChange = (value) => {
    setToolJsonText(value);
    try {
      JSON.parse(value);
      setToolJsonError(null);
    } catch (e) {
      setToolJsonError(e.message);
    }
  };

  const selectExample = (exampleKey) => {
    const example = TOOL_EXAMPLES[exampleKey];
    setToolJsonText(JSON.stringify(example, null, 2));
    setToolJsonError(null);
    setShowExamplesDropdown(false);
  };

  const generateToolSchema = async () => {
    if (!showGeneratorInput) {
      // Show the input field first
      setShowGeneratorInput(true);
      return;
    }

    if (!generatorPrompt.trim() || generatorPrompt.length < 10) {
      toast.error(t('errors.descMinLength'));
      return;
    }

    setIsGenerating(true);
    try {
      const response = await api.post('/tool-generator', { prompt: generatorPrompt });

      if (response.data.success && response.data.schema) {
        const tool = response.data.schema;
        setToolJsonText(JSON.stringify(tool, null, 2));
        setToolJsonError(null);
        toast.success(t('success.toolGenerated'));
        setGeneratorPrompt('');
        setShowGeneratorInput(false);
      } else {
        toast.error(t('errors.failedGenerateTool'));
      }
    } catch (error) {
      console.error('Error generating tool:', error);
      const errorMessage = error.response?.data?.detail || t('errors.failedGenerateTool');
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateTool = async () => {
    // Validate JSON
    let toolDef;
    try {
      toolDef = JSON.parse(toolJsonText);
    } catch (e) {
      toast.error(t('errors.invalidJson') + ': ' + e.message);
      return;
    }

    // Validate required fields
    if (!toolDef.name || !toolDef.description) {
      toast.error(t('errors.toolNeedNameDesc'));
      return;
    }

    // Webhook URL is optional - if empty, tool will be AI-handled

    try {
      setCreatingTool(true);

      const toolData = {
        name: toolDef.name,
        description: toolDef.description,
        webhook_url: webhookUrl || null,  // Send null if empty for AI-handled tools
        webhook_timeout_seconds: 10,
        parameters: toolDef.parameters || {
          type: 'object',
          properties: {},
          required: []
        },
        is_active: true
      };

      if (editingToolId) {
        // Update existing tool
        await api.put(`/custom-tools/${editingToolId}`, toolData);
        toast.success(t('success.toolUpdated'));
      } else {
        // Create new tool
        await api.post('/custom-tools', toolData);
        toast.success(t('success.toolCreated'));
        // Auto-select the new tool
        setFormData(prev => ({
          ...prev,
          tools: [...prev.tools, toolDef.name]
        }));
      }

      setShowToolModal(false);
      setEditingToolId(null);

      // Refresh available tools
      fetchAvailableTools();

    } catch (error) {
      console.error('Error saving tool:', error);
      toast.error(error.response?.data?.detail || t('errors.failedSaveTool'));
    } finally {
      setCreatingTool(false);
    }
  };

  const formatBytes = (bytes = 0) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / (1024 ** exponent);
    return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
  };

  const formatDocumentDate = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  const uploadHoldMusic = async (file) => {
    if (!id) {
      toast.error(t('agentEditor.holdMusicSaveFirst'));
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await api.post(`/agents/${id}/hold-music/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((prev) => ({ ...prev, handoff_hold_music_url: response.data.url }));
      toast.success('Música de espera subida correctamente');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || `Error subiendo archivo: ${err.message}`);
    }
  };

  const handleKnowledgeBaseUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !id) {
      event.target.value = '';
      return;
    }

    const allowedExtensions = ['txt', 'md', 'csv', 'json'];
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      toast.error(t('agentEditor.invalidKnowledgeBaseFileType', 'Only .txt, .md, .csv, and .json files are supported'));
      event.target.value = '';
      return;
    }

    try {
      setKbUploading(true);
      const payload = new FormData();
      payload.append('file', file);
      const response = await api.post(`/agents/${id}/knowledge-base/documents`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setKnowledgeBaseDocuments(response.data.documents || []);
      setKnowledgeBaseTotalSize(response.data.total_size || 0);
      setFormData(prev => ({ ...prev, knowledge_base: response.data.knowledge_base || prev.knowledge_base }));
      toast.success(t('agentEditor.knowledgeBaseUploadSuccess', 'Document uploaded'));
    } catch (error) {
      console.error('Error uploading knowledge base document:', error);
      toast.error(error.response?.data?.detail || t('agentEditor.knowledgeBaseUploadError', 'Failed to upload document'));
    } finally {
      setKbUploading(false);
      event.target.value = '';
    }
  };

  const handleKnowledgeBaseDownload = async (doc) => {
    if (!id || !doc?.id) return;

    try {
      const response = await api.get(`/agents/${id}/knowledge-base/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', doc.name || 'document.txt');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading knowledge base document:', error);
      toast.error(error.response?.data?.detail || t('agentEditor.knowledgeBaseDownloadError', 'Failed to download document'));
    }
  };

  const handleKnowledgeBaseDelete = async (doc) => {
    if (!id || !doc?.id) return;
    if (!window.confirm(t('agentEditor.deleteKnowledgeBaseDocumentConfirm', { defaultValue: `Delete ${doc.name}?`, name: doc.name }))) {
      return;
    }

    try {
      const response = await api.delete(`/agents/${id}/knowledge-base/documents/${doc.id}`);
      setKnowledgeBaseDocuments(response.data.documents || []);
      setKnowledgeBaseTotalSize(response.data.total_size || 0);
      setFormData(prev => ({ ...prev, knowledge_base: response.data.knowledge_base || '' }));
      toast.success(t('agentEditor.knowledgeBaseDeleteSuccess', 'Document deleted'));
    } catch (error) {
      console.error('Error deleting knowledge base document:', error);
      toast.error(error.response?.data?.detail || t('agentEditor.knowledgeBaseDeleteError', 'Failed to delete document'));
    }
  };

  const handleDeleteTool = async (toolId, toolName) => {
    if (!window.confirm(t('errors.deleteToolConfirm', { name: toolName }))) {
      return;
    }

    try {
      await api.delete(`/custom-tools/${toolId}`);
      toast.success(t('success.toolDeleted'));

      // Remove from selected tools if it was selected
      setFormData(prev => ({
        ...prev,
        tools: prev.tools.filter(t => t !== toolName)
      }));

      // Refresh available tools
      fetchAvailableTools();
    } catch (error) {
      console.error('Error deleting tool:', error);
      toast.error(error.response?.data?.detail || t('errors.failedDeleteTool'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.instructions.trim()) {
      toast.error(t('errors.nameAndInstructions'));
      return;
    }

    if (isChildMode && !isValidE164(formData.handoff_primary_phone)) {
      toast.error(t('agentEditor.childPhoneRequired', 'Selecciona un número de handoff'));
      document.getElementById('handoff_primary_phone')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...formData,
        region: normalizeRegion(formData.region),
        audio_filter_preset: 'coffee_shop',
        // Strip internal _confirmed flags before sending to API
        handoff_directory: (formData.handoff_directory || []).map(({ _confirmed, ...entry }) => entry),
      };
      if (!isEditing && isChildMode && knowledgeBaseDocuments.length > 0) {
        payload.knowledge_base_documents = knowledgeBaseDocuments;
      } else {
        delete payload.knowledge_base_documents;
      }
      delete payload.knowledge_base_total_size;
      delete payload.allowed_tenants;
      delete payload.is_shared;
      delete payload.tenant_id;
      delete payload.created_at;
      delete payload.updated_at;

      let agentId = id;
      if (isEditing) {
        await api.put(`/agents/${id}`, payload);
        toast.success(t('success.agentUpdated'));
      } else {
        const response = await api.post('/agents', payload);
        agentId = response.data.id;
        toast.success(t('success.agentCreated'));
      }

      // Save allowed tenants (super_admin only)
      if (isSuperAdmin && agentId) {
        await api.put(`/agents/${agentId}/allowed-tenants`, { allowed_tenants: allowedTenants });
      }

      navigate('/agents');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isEditing ? t('errors.failedUpdateAgentFull') : t('errors.failedCreateAgent')));
      console.error('Error saving agent:', error);
    } finally {
      setSaving(false);
    }
  };

  const voiceOptions = [
    { value: 'marin', label: 'Marin (Female, Spanish)' },
    { value: 'nova', label: 'Nova (Female, English)' },
    { value: 'alloy', label: 'Alloy (Neutral, English)' },
    { value: 'echo', label: 'Echo (Male, English)' },
    { value: 'fable', label: 'Fable (Male, English)' },
    { value: 'onyx', label: 'Onyx (Male, English)' },
    { value: 'shimmer', label: 'Shimmer (Female, English)' },
  ];

  const languageOptions = [
    { value: 'es-MX', label: 'Spanish (Mexico)' },
    { value: 'es-ES', label: 'Spanish (Spain)' },
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'fr-FR', label: 'French' },
    { value: 'de-DE', label: 'German' },
    { value: 'it-IT', label: 'Italian' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/agents')}
            className="mr-4" style={{ color: 'var(--muted)' }}
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-foreground sm:text-3xl sm:truncate">
              {isEditing ? t('agentEditor.editAgent') : t('agentEditor.createAgent')}
            </h2>
            <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
              {isEditing ? t('agentEditor.updateConfig') : t('agentEditor.configureAgent')}
            </p>
          </div>
        </div>
      </div>

      {/* Child Agent Banner — create mode */}
      {isChildMode && parentAgent && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-primary/30 bg-primary/5">
          <span className="text-primary text-lg">🔗</span>
          <p className="text-sm font-medium text-foreground">
            {t('agentEditor.childBanner', 'Child of')}: <strong>{parentAgent.name}</strong>
          </p>
          <p className="text-xs text-muted ml-auto">
            {t('agentEditor.lockedField', 'Inherited from parent — edit parent to change')}
          </p>
        </div>
      )}

      {/* Child Agent Banner — edit mode (isChild = already saved with parent_agent_id) */}
      {isChild && !isChildMode && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-primary/30 bg-primary/5">
          <span className="text-primary text-lg">🔗</span>
          <p className="text-sm font-medium text-foreground">
            {t('agentEditor.childBanner', 'Heredado del agente padre')}:{' '}
            <strong>{allAgents.find(a => a.id === formData.parent_agent_id)?.name || formData.parent_agent_id}</strong>
          </p>
          <p className="text-xs text-muted ml-auto">
            {t('agentEditor.lockedField', 'Campos heredados bloqueados — edita el padre para cambiarlos')}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Basic Information */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('agentEditor.basicInfo')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground">
                    {t('agentEditor.agentName')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input-field mt-1"
                    placeholder={t('agentEditor.agentNamePlaceholder')}
                    required
                  />
                </div>

                <MaybeLocked locked={isChildMode || isChild}>
                <div>
                  <label htmlFor="agent_type" className="block text-sm font-medium text-foreground">
                    {t('agentEditor.agentType') || 'Agent Type'}
                  </label>
                  <select
                    name="agent_type"
                    id="agent_type"
                    value={formData.agent_type || 'general'}
                    onChange={handleInputChange}
                    className="input-field mt-1"
                  >
                    <option value="general">{t('agentEditor.typeGeneral') || 'General'}</option>
                    <option value="debt_collection">{t('agentEditor.typeDebtCollection') || 'Debt Collection (Cobranza)'}</option>
                    <option value="sales">{t('agentEditor.typeSales') || 'Sales (Ventas)'}</option>
                  </select>
                  <p className="mt-1 text-xs text-muted">
                    {formData.agent_type === 'debt_collection'
                      ? (t('agentEditor.typeDebtCollectionDesc') || 'Adds privacy, compliance, anti-deflection, and negotiation rules automatically.')
                      : formData.agent_type === 'sales'
                      ? (t('agentEditor.typeSalesDesc') || 'Standard sales agent configuration.')
                      : (t('agentEditor.typeGeneralDesc') || 'No industry-specific rules applied.')}
                  </p>
                </div>
                </MaybeLocked>

                <MaybeLocked locked={isChildMode || isChild}>
                <div>
                  <label htmlFor="department" className="block text-sm font-medium text-foreground">
                    {t('agentEditor.department') || 'Departamento'}
                  </label>
                  <select
                    name="department"
                    id="department"
                    value={formData.department || 'ventas_cobranza'}
                    onChange={handleInputChange}
                    className="input-field mt-1"
                  >
                    <option value="ventas_cobranza">{t('agentEditor.departmentVentasCobranza') || 'Ventas / Cobranza'}</option>
                    <option value="postventa">{t('agentEditor.departmentPostventa') || 'Postventa'}</option>
                  </select>
                </div>
                </MaybeLocked>

                {/* ── Propietario (salesperson) ──────────────────── */}
                {salespersonProfiles.length > 0 && (
                  <div>
                    <label htmlFor="owner_salesperson_id" className="block text-sm font-medium text-foreground">
                      {t('agentEditor.ownerSalesperson', 'Propietario (vendedor)')}
                    </label>
                    <select
                      id="owner_salesperson_id"
                      name="owner_salesperson_id"
                      value={formData.owner_salesperson_id || ''}
                      onChange={handleInputChange}
                      className="input-field mt-1"
                    >
                      <option value="">{t('agentEditor.ownerNone', 'Sin propietario (compartido)')}</option>
                      {salespersonProfiles.map(p => (
                        <option key={p.profile_id} value={p.profile_id}>{p.name}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-muted">
                      {t('agentEditor.ownerSalespersonDesc', 'El vendedor que aparece como organizador en las citas de este agente.')}
                    </p>
                  </div>
                )}

                {/* ── Transferencia humana ─────────────────────────────── */}
                {!isChild && !isChildMode && <div className="border rounded-lg p-4 space-y-4" style={{ borderColor: 'var(--border)' }}>
                  <h3 className="text-sm font-semibold text-foreground">{t('agentEditor.humanTransfer')}</h3>

                  {/* Primary name — hidden for parent agents (they delegate to child handoff directories) */}
                  {!isParent && (
                  <div>
                    <label className="block text-sm font-medium text-foreground">{t('agentEditor.primaryContactName')}</label>
                    <input
                      type="text"
                      className="input-field mt-1"
                      placeholder={t('agentEditor.primaryContactNamePlaceholder')}
                      value={formData.handoff_primary_name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, handoff_primary_name: e.target.value }))}
                    />
                  </div>
                  )}

                  {/* Hold music + ring timeout */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-foreground">{t('agentEditor.holdMusic')}</label>
                      <div
                        className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-indigo-400"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={async (e) => {
                          e.preventDefault();
                          const f = e.dataTransfer.files?.[0];
                          if (f) await uploadHoldMusic(f);
                        }}
                        onClick={() => document.getElementById('hold-music-input').click()}
                      >
                        {formData.handoff_hold_music_url ? (
                          <div>
                            <audio controls src={formData.handoff_hold_music_url} className="w-full" onClick={(e) => e.stopPropagation()} />
                            <p className="text-xs mt-1 text-gray-500 truncate">{formData.handoff_hold_music_url}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">{t('agentEditor.holdMusicDropHint')}</p>
                        )}
                        <input
                          id="hold-music-input"
                          type="file"
                          accept="audio/mpeg,audio/mp3,audio/wav"
                          className="hidden"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (f) await uploadHoldMusic(f);
                            e.target.value = '';
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('agentEditor.ringTimeout')}</label>
                      <input
                        type="number"
                        className="input-field mt-1"
                        min={5} max={120}
                        value={formData.handoff_ring_timeout_seconds ?? 30}
                        onChange={(e) => setFormData(prev => ({ ...prev, handoff_ring_timeout_seconds: parseInt(e.target.value) || 30 }))}
                      />
                    </div>
                  </div>

                  {/* No-answer message */}
                  <div>
                    <label className="block text-sm font-medium text-foreground">{t('agentEditor.noAnswerMessage')}</label>
                    <input
                      type="text"
                      className="input-field mt-1"
                      placeholder={t('agentEditor.noAnswerMessagePlaceholder')}
                      value={formData.handoff_no_answer_message || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, handoff_no_answer_message: e.target.value }))}
                    />
                  </div>

                  {/* Handoff directory */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-foreground">{t('agentEditor.handoffDirectory')}</label>
                      <button
                        type="button"
                        className="text-xs px-2 py-1 rounded border flex items-center gap-1"
                        style={{ borderColor: 'var(--border)', color: 'var(--primary)' }}
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          handoff_directory: [...(prev.handoff_directory || []), { name: '', phone: '', _confirmed: false }],
                        }))}
                      >
                        <PlusIcon className="h-3 w-3" /> {t('agentEditor.addRow')}
                      </button>
                    </div>
                    {(formData.handoff_directory || []).length === 0 ? (
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>{t('agentEditor.noAdditionalEntries')}</p>
                    ) : (
                      <div className="space-y-2">
                        {(formData.handoff_directory || []).map((entry, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input
                              type="text"
                              className="input-field flex-1"
                              placeholder="Nombre"
                              value={entry.name || ''}
                              onChange={(e) => setFormData(prev => {
                                const dir = [...(prev.handoff_directory || [])];
                                dir[idx] = { ...dir[idx], name: e.target.value, _confirmed: false };
                                return { ...prev, handoff_directory: dir };
                              })}
                            />
                            <input
                              type="text"
                              className="input-field flex-1"
                              placeholder="+521…"
                              value={entry.phone || ''}
                              onChange={(e) => setFormData(prev => {
                                const dir = [...(prev.handoff_directory || [])];
                                dir[idx] = { ...dir[idx], phone: e.target.value, _confirmed: false };
                                return { ...prev, handoff_directory: dir };
                              })}
                            />
                            <span
                              className="text-xs whitespace-nowrap px-2 py-0.5 rounded-full"
                              style={{
                                background: entry._confirmed ? 'var(--success-bg, #d1fae5)' : 'var(--warn-bg, #fef3c7)',
                                color: entry._confirmed ? 'var(--success, #065f46)' : 'var(--warn, #92400e)',
                              }}
                            >
                              {entry._confirmed ? t('agentEditor.savedEntry') : t('agentEditor.unsavedEntry')}
                            </span>
                            <button
                              type="button"
                              className="text-red-400 hover:text-red-600"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                handoff_directory: (prev.handoff_directory || []).filter((_, i) => i !== idx),
                              }))}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Section-level save */}
                  {isEditing && (
                    <button
                      type="button"
                      className="btn-secondary text-sm w-full"
                      disabled={handoffDirSaving}
                      onClick={handleSaveHandoffSection}
                    >
                      {handoffDirSaving ? t('agentEditor.savingHandoff') : t('agentEditor.saveHandoffSection')}
                    </button>
                  )}
                </div>}

                <MaybeLocked locked={isChildMode || isChild}>
                <div>
                  <label htmlFor="region" className="block text-sm font-medium text-foreground">
                    {t('agentEditor.region')}
                  </label>
                  <select
                    name="region"
                    id="region"
                    value={normalizeRegion(formData.region)}
                    onChange={handleInputChange}
                    className="input-field mt-1"
                  >
                    {REGION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.value === 'none' ? t('agentEditor.regionNone') : option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
                    {t('agentEditor.regionHelp')}
                  </p>
                </div>
                </MaybeLocked>

                <MaybeLocked locked={isChildMode || isChild}>
                <div>
                  <label htmlFor="instructions" className="block text-sm font-medium text-foreground">
                    {t('agentEditor.agentInstructions')}
                  </label>
                  <div className="relative mt-1">
                    <textarea
                      ref={instructionsRef}
                      name="instructions"
                      id="instructions"
                      rows={12}
                      value={formData.instructions}
                      onChange={handleInstructionsChange}
                      onKeyDown={(e) => {
                        // Handle keyboard navigation in dropdown
                        if (showVarDropdown && e.key === 'Escape') {
                          setShowVarDropdown(false);
                          e.preventDefault();
                        }
                      }}
                      className="input-field w-full"
                      placeholder={`Define the agent's purpose and behavior.

For campaign calls, use @{variable} to insert contact data:

Hola, ¿hablo con @{name}? Le llamo de Empresa X
respecto a su crédito @{producto} con saldo de @{deuda}.

Type @{ to see available variables.`}
                      required
                    />

                    {/* Autocomplete dropdown */}
                    {showVarDropdown && (
                      <div
                        className="absolute z-10 bg-card border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto"
                        style={{ top: varDropdownPos.top, left: varDropdownPos.left, minWidth: '220px' }}
                      >
                        {allVariables
                          .filter(v => !varFilter || v.key.toLowerCase().includes(varFilter))
                          .map(v => (
                            <button
                              key={v.key}
                              type="button"
                              onClick={() => insertVariable(v.key)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-700 flex items-center justify-between"
                            >
                              <span className="font-mono font-medium">@{'{' + v.key + '}'}</span>
                              <span className="text-xs ml-2" style={{ color: 'var(--muted)' }}>{v.label}</span>
                            </button>
                          ))
                        }
                        {allVariables.filter(v => !varFilter || v.key.toLowerCase().includes(varFilter)).length === 0 && (
                          <div className="px-3 py-2 text-sm" style={{ color: 'var(--muted)' }}>{t('agentEditor.noMatchingVars')}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Variable chips panel */}
                  {allVariables.length > 0 && (
                    <div className="mt-3 p-3 bg-background rounded-xl border border-border">
                      <div className="flex items-center mb-2">
                        <AtSymbolIcon className="h-4 w-4 mr-1.5" style={{ color: 'var(--muted)' }} />
                        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted)' }}>
                          {t('agentEditor.dynamicVariables')}
                        </span>
                        <span className="ml-2 text-xs" style={{ color: 'var(--muted)' }}>
                          {t('agentEditor.clickToInsert')}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {primarySystemVars.map(v => (
                          <button
                            key={v.key}
                            type="button"
                            onClick={() => {
                              const textarea = instructionsRef.current;
                              if (textarea) textarea.focus();
                              insertVariable(v.key);
                            }}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-mono bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                            title={v.label}
                          >
                            @{'{' + v.key + '}'}
                          </button>
                        ))}
                        {(promptVariables.custom || []).map(v => (
                          <button
                            key={v.key}
                            type="button"
                            onClick={() => {
                              const textarea = instructionsRef.current;
                              if (textarea) textarea.focus();
                              insertVariable(v.key);
                            }}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-mono bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                            title={`From CSV/webhook: ${v.key}`}
                          >
                            @{'{' + v.key + '}'}
                          </button>
                        ))}
                      </div>
                      <p className="mt-2 text-xs" style={{ color: 'var(--muted)' }}>
                        <span className="inline-block w-2 h-2 rounded-full bg-blue-200 mr-1"></span> {t('agentEditor.contactFields')}
                        <span className="inline-block w-2 h-2 rounded-full bg-purple-200 mr-1 ml-3"></span> {t('agentEditor.customFields')}
                      </p>
                    </div>
                  )}

                  <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}
                    dangerouslySetInnerHTML={{ __html: t('agentEditor.instructionsHelp') }}
                  />
                </div>
                </MaybeLocked>

                {/* Inbound Instructions */}
                <MaybeLocked locked={isChildMode || isChild}>
                <div>
                  <label htmlFor="inbound_instructions" className="block text-sm font-medium text-foreground">
                    {t('agentEditor.outboundInstructions', 'Inbound Instructions (optional)')}
                  </label>
                  <p className="text-xs mb-1" style={{ color: 'var(--muted)' }}>
                    {t('agentEditor.outboundInstructionsHelp', 'If set, these instructions will be used when the agent receives inbound calls. Outbound calls will use the main instructions above.')}
                  </p>
                  <textarea
                    name="inbound_instructions"
                    id="inbound_instructions"
                    rows={8}
                    value={formData.inbound_instructions}
                    onChange={(e) => setFormData(prev => ({ ...prev, inbound_instructions: e.target.value }))}
                    className="input-field w-full mt-1"
                    placeholder={t('agentEditor.outboundInstructionsPlaceholder', 'Leave empty to use the same instructions for both inbound and outbound calls.\n\nExample: Different customer-service handling for inbound vs sales pitch for outbound.')}
                  />
                </div>
                </MaybeLocked>
              </div>
            </div>

            {/* Knowledge Base */}
            <div className="card">
              <MaybeLocked locked={isChildMode || isChild}>
              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-foreground">
                      {t('agentEditor.knowledgeBase', 'Knowledge Base')}
                    </h3>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                      {t('agentEditor.knowledgeBaseHelp', 'Upload reference documents the agent can use during calls. The system keeps track of each file and builds the final knowledge base automatically.')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800" style={{ color: 'var(--muted)' }}>
                      {t('agentEditor.totalKnowledgeBaseSize', 'Total size')}: {formatBytes(knowledgeBaseTotalSize)}
                    </span>
                    <input
                      ref={kbUploadInputRef}
                      type="file"
                      accept=".txt,.md,.csv,.json"
                      className="hidden"
                      onChange={handleKnowledgeBaseUpload}
                      disabled={!isEditing || kbUploading}
                    />
                    <button
                      type="button"
                      onClick={() => kbUploadInputRef.current?.click()}
                      disabled={!isEditing || kbUploading}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
                      {kbUploading ? t('agentEditor.uploadingKnowledgeBaseDocument', 'Uploading...') : t('agentEditor.uploadKnowledgeBaseDocument', 'Upload Document')}
                    </button>
                  </div>
                </div>

                {!isEditing && (
                  <div className="rounded-lg border border-dashed p-4 text-sm" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
                    {t('agentEditor.saveAgentBeforeKnowledgeBaseUpload', 'Save the agent first, then you can upload knowledge base documents.')}
                  </div>
                )}

                {isEditing && kbLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : knowledgeBaseDocuments.length > 0 ? (
                  <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--border)' }}>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y" style={{ borderColor: 'var(--border)' }}>
                        <thead className="bg-slate-50 dark:bg-slate-900/40">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted)' }}>
                              {t('agentEditor.documentName', 'Document')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted)' }}>
                              {t('agentEditor.documentSize', 'Size')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted)' }}>
                              {t('agentEditor.documentUploadedAt', 'Uploaded')}
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted)' }}>
                              {t('agentEditor.actions', 'Actions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                          {knowledgeBaseDocuments.map((doc) => (
                            <tr key={doc.id} className="bg-white dark:bg-transparent">
                              <td className="px-4 py-3 text-sm text-foreground">
                                <div className="flex items-center gap-3">
                                  <DocumentTextIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--muted)' }} />
                                  <span className="break-all">{doc.name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm" style={{ color: 'var(--muted)' }}>
                                {formatBytes(doc.size)}
                              </td>
                              <td className="px-4 py-3 text-sm" style={{ color: 'var(--muted)' }}>
                                {formatDocumentDate(doc.uploaded_at)}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleKnowledgeBaseDownload(doc)}
                                    className="inline-flex items-center rounded-md px-2.5 py-1.5 text-xs font-medium border hover:bg-slate-50 dark:hover:bg-slate-900/40"
                                    style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                                  >
                                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                    {t('agentEditor.downloadKnowledgeBaseDocument', 'Download')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleKnowledgeBaseDelete(doc)}
                                    className="inline-flex items-center rounded-md px-2.5 py-1.5 text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-950/30"
                                  >
                                    <TrashIcon className="h-4 w-4 mr-1" />
                                    {t('agentEditor.deleteKnowledgeBaseDocument', 'Delete')}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-6 text-sm text-center" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
                    {t('agentEditor.noKnowledgeBaseDocuments', 'No knowledge base documents uploaded yet.')}
                  </div>
                )}
              </div>
              </MaybeLocked>
            </div>

            {/* Tools */}
            <div className="card">
              <MaybeLocked locked={isChildMode || isChild}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">{t('agentEditor.availableTools')}</h3>
                <button
                  type="button"
                  onClick={openToolModal}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary whitespace-nowrap shrink-0"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  {t('agentEditor.addTool')}
                </button>
              </div>
              <div className="space-y-6">
                {Object.keys(availableTools).length > 0 ? (
                  Object.entries(availableTools).map(([category, tools]) => (
                    <div key={category}>
                      <h4 className="text-sm font-semibold uppercase tracking-wide mb-3 flex items-center" style={{ color: 'var(--muted)' }}>
                        {category.replace(/_/g, ' ')}
                        {category === 'custom' && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                            Custom
                          </span>
                        )}
                      </h4>
                      <div className="space-y-2 pl-2">
                        {tools.map((tool) => (
                          <div key={tool.name} className="flex items-start justify-between group">
                            <label className="flex items-start cursor-pointer flex-1">
                              <input
                                type="checkbox"
                                checked={formData.tools.includes(tool.name)}
                                onChange={() => handleToolToggle(tool.name)}
                                className="h-4 w-4 mt-0.5 text-primary-600 focus:ring-primary border-border rounded"
                              />
                              <div className="ml-3">
                                <span className="text-sm font-medium text-foreground flex items-center">
                                  {tool.name}
                                  {tool.type === 'custom' && (
                                    <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                                      Custom
                                    </span>
                                  )}
                                </span>
                                <p className="text-xs" style={{ color: 'var(--muted)' }}>{tool.description}</p>
                              </div>
                            </label>
                            {tool.type === 'custom' && tool.tool_id && (
                              <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  type="button"
                                  onClick={() => openEditToolModal(tool)}
                                  className="p-1 hover:text-blue-600" style={{ color: 'var(--muted)' }}
                                  title={t('tools.editTool')}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteTool(tool.tool_id, tool.name)}
                                  className="p-1 hover:text-red-600" style={{ color: 'var(--muted)' }}
                                  title={t('tools.deleteTool')}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm" style={{ color: 'var(--muted)' }}>{t('agentEditor.noToolsAvailable')}</p>
                )}
              </div>
              </MaybeLocked>
            </div>
          </div>

          {/* Configuration */}
          <div className="space-y-6">
            <div className="card">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('agentEditor.configuration')}</h3>
              
              <div className="space-y-4">
                {/* ElevenLabs Voice Configuration */}
                <MaybeLocked locked={isChildMode || isChild}>
                <div className="space-y-4 p-4 rounded-xl border" style={{ borderColor: 'var(--border)', background: 'var(--glass-bg)' }}>
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      {t('agentEditor.womanVoice')}
                    </label>
                    {loadingVoices ? (
                      <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>Loading voices...</p>
                    ) : (
                      <select
                        name="elevenlabs_voice_id"
                        value={formData.elevenlabs_voice_id || ''}
                        onChange={handleInputChange}
                        className="input-field mt-1"
                      >
                        <option value="">-- Select a voice --</option>
                        {elevenlabsVoices.filter(v => v.name.toLowerCase().includes('marlene')).map(v => (
                          <option key={v.voice_id} value={v.voice_id}>
                            {v.name} ({v.category})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      {t('agentEditor.manVoice')}
                    </label>
                    {loadingVoices ? (
                      <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>Loading voices...</p>
                    ) : (
                      <select
                        name="elevenlabs_voice_id_secondary"
                        value={formData.elevenlabs_voice_id_secondary || ''}
                        onChange={handleInputChange}
                        className="input-field mt-1"
                      >
                        <option value="">{t('agentEditor.secondaryVoiceNone')}</option>
                        {elevenlabsVoices.filter(v => !v.name.toLowerCase().includes('marlene')).map(v => (
                          <option key={v.voice_id} value={v.voice_id}>
                            {v.name} ({v.category})
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
                      {t('agentEditor.manVoiceHelp')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      {t('agentEditor.elevenlabsModel')}
                    </label>
                    <select
                      name="elevenlabs_model_id"
                      value={formData.elevenlabs_model_id || 'eleven_flash_v2_5'}
                      onChange={handleInputChange}
                      className="input-field mt-1"
                    >
                      <option value="eleven_flash_v2_5">Flash v2.5 (Low Latency)</option>
                      <option value="eleven_multilingual_v2">Multilingual v2 (High Quality)</option>
                      <option value="eleven_turbo_v2_5">Turbo v2.5 (Fastest)</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Stability: {Number(formData.elevenlabs_voice_settings?.stability ?? 0.5).toFixed(2)}
                      </label>
                      <input
                        type="range" min="0" max="1" step="0.05"
                        value={formData.elevenlabs_voice_settings?.stability ?? 0.5}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          elevenlabs_voice_settings: {
                            ...prev.elevenlabs_voice_settings,
                            stability: parseFloat(e.target.value)
                          }
                        }))}
                        className="w-full mt-1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Similarity: {Number(formData.elevenlabs_voice_settings?.similarity_boost ?? 0.75).toFixed(2)}
                      </label>
                      <input
                        type="range" min="0" max="1" step="0.05"
                        value={formData.elevenlabs_voice_settings?.similarity_boost ?? 0.75}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          elevenlabs_voice_settings: {
                            ...prev.elevenlabs_voice_settings,
                            similarity_boost: parseFloat(e.target.value)
                          }
                        }))}
                        className="w-full mt-1"
                      />
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        {t('agentEditor.naturalFillers')}
                      </label>
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>{t('agentEditor.naturalFillersHelp')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.natural_fillers}
                        onChange={(e) => setFormData(prev => ({ ...prev, natural_fillers: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor="language" className="block text-sm font-medium text-foreground">
                    {t('agentEditor.languageLabel')}
                  </label>
                  <select
                    name="language"
                    id="language"
                    value={formData.language}
                    onChange={handleInputChange}
                    className="input-field mt-1"
                  >
                    {languageOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="sensitivity_preset" className="block text-sm font-medium text-foreground">
                    {t('agentEditor.voiceSensitivity')}
                  </label>
                  <select
                    name="sensitivity_preset"
                    id="sensitivity_preset"
                    value={formData.sensitivity_preset}
                    onChange={handleInputChange}
                    className="input-field mt-1"
                  >
                    <option value="responsive">{t('agents.sensitivityResponsive')}</option>
                    <option value="balanced">{t('agents.sensitivityBalanced')}</option>
                    <option value="conservative">{t('agents.sensitivityConservative')}</option>
                    <option value="custom_server">{t('agents.sensitivityCustom')}</option>
                  </select>
                  <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>
                    {t('agentEditor.sensitivityHelp')}
                  </p>
                </div>
                </MaybeLocked>

              </div>
            </div>

            {/* Integraciones */}
            <div className="card">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('agentEditor.integrations')}</h3>
              <MaybeLocked locked={isChildMode || isChild}>

              {/* Google Calendar via Composio */}
              <div className="border-b border-border pb-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-foreground">
                      {t('agentEditor.enableGoogleCalendar')}
                    </label>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      {t('agentEditor.composioToolkit')}
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary border-border rounded"
                      checked={Boolean(formData.composio_integration?.enabled)}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        composio_integration: {
                          ...(prev.composio_integration || { toolkit: 'GOOGLECALENDAR', connected_account_id: '' }),
                          enabled: e.target.checked,
                        },
                      }))}
                    />
                  </label>
                </div>

                {(formData.composio_integration?.enabled || composioStatus?.connected) && (
                  <div className="mt-3 space-y-2">
                    {/* Live connection status */}
                    {composioStatusLoading ? (
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>{t('agentEditor.composioLoading')}</p>
                    ) : composioStatus?.connected ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800">
                          <span>&#10003;</span>
                          <span>{t('agentEditor.composioConnected', { email: composioStatus.email ? `: ${composioStatus.email}` : ' a Google Calendar' })}</span>
                        </span>
                        <button
                          type="button"
                          onClick={handleComposioDisconnect}
                          disabled={composioDisconnecting || !isEditing}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded border border-red-400 text-red-600 hover:bg-red-50 disabled:opacity-50"
                          title={!isEditing ? t('agentEditor.composioSaveFirst') : t('agentEditor.composioDisconnect')}
                        >
                          {composioDisconnecting ? t('agentEditor.composioDisconnecting') : t('agentEditor.composioDisconnect')}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {t('agentEditor.composioNotConnected')}
                        </span>
                        <div>
                          <button
                            type="button"
                            onClick={handleComposioConnect}
                            disabled={composioConnecting || !isEditing}
                            className="btn-secondary text-sm disabled:opacity-50"
                            title={!isEditing ? t('agentEditor.composioSaveFirst') : ''}
                          >
                            {composioConnecting ? t('agentEditor.composioConnecting') : t('agentEditor.composioConnect')}
                          </button>
                        </div>
                      </div>
                    )}
                    {!isEditing && (
                      <p className="text-xs italic" style={{ color: 'var(--muted)' }}>
                        {t('agentEditor.composioSaveFirst')}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* SMS / Mensajes de Texto */}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-foreground">
                      {t('agentEditor.enableSms')}
                    </label>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      {t('agentEditor.smsHelp')}
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary border-border rounded"
                      checked={Boolean(formData.sms_enabled)}
                      onChange={(e) => setFormData(prev => ({ ...prev, sms_enabled: e.target.checked }))}
                    />
                  </label>
                </div>

                {formData.sms_enabled && (
                  <div className="mt-3">
                    <label className="text-xs font-medium text-foreground">{t('agentEditor.smsNumber')}</label>
                    <input
                      type="text"
                      className="mt-1 w-full text-sm border border-border rounded px-2 py-1"
                      placeholder="+17402738774"
                      value={formData.sms_from}
                      onChange={(e) => setFormData(prev => ({ ...prev, sms_from: e.target.value }))}
                    />
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                      {t('agentEditor.smsNumberHelp')}
                    </p>
                  </div>
                )}
              </div>

              {/* WhatsApp via Twilio */}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-foreground">
                      {t('agentEditor.enableWhatsapp')}
                    </label>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      {t('agentEditor.whatsappHelp')}
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary border-border rounded"
                      checked={Boolean(formData.whatsapp_enabled)}
                      onChange={(e) => setFormData(prev => ({ ...prev, whatsapp_enabled: e.target.checked }))}
                    />
                  </label>
                </div>

                {formData.whatsapp_enabled && (
                  <div className="mt-3 text-sm">
                    <p style={{ color: 'var(--muted)' }}>
                      {t('agentEditor.whatsappNumber')}{' '}
                      <span className="font-medium text-foreground">
                        {tenantInfo?.twilio_whatsapp_from || t('agentEditor.notConfigured')}
                      </span>
                    </p>
                    {!tenantInfo?.twilio_whatsapp_from && (
                      <button
                        type="button"
                        onClick={() => navigate('/settings')}
                        className="text-xs text-primary-600 hover:text-primary-500 underline mt-1"
                      >
                        {t('agentEditor.configureNumber')}
                      </button>
                    )}
                  </div>
                )}
              </div>
              </MaybeLocked>
            </div>

            {/* Agent Ownership & Hierarchy */}
            <div className="card">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('agentEditor.ownership')}</h3>
              <div className="space-y-4">
                {/* Owner dropdown — always visible; auto-defaults to current user */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('agentEditor.owner')}
                  </label>
                  <select
                    className="input w-full"
                    value={formData.owner_user_id}
                    onChange={e => {
                      const uid = e.target.value;
                      const owner = teamUsers.find(u => u.user_id === uid);
                      setFormData(prev => ({
                        ...prev,
                        owner_user_id: uid,
                        handoff_primary_phone: (owner?.phone) || prev.handoff_primary_phone,
                      }));
                    }}
                  >
                    <option value="">{t('agentEditor.ownerNone')}</option>
                    {teamUsers.map(u => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.name} ({u.email})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{t('agentEditor.ownerHelp')}</p>
                </div>

                {/* Primary handoff phone — hidden for parent agents; highlighted red for child agents until valid */}
                {!isParent && <div id="handoff_primary_phone" className={childPhoneMissing ? 'ring-2 ring-red-500 rounded-lg p-3' : ''}>
                  <label className={`block text-sm font-medium mb-1 ${childPhoneMissing ? 'text-red-600' : 'text-foreground'}`}>
                    {(isChild || isChildMode)
                      ? t('agentEditor.childHandoffPhone', 'Número de transferencia de este agente hijo')
                      : t('agentEditor.primaryHandoffPhone')}
                    {childPhoneMissing && (
                      <span className="ml-2 text-xs font-normal">
                        ⚠️ {t('agentEditor.childPhoneRequired', 'Required for child agents')}
                      </span>
                    )}
                  </label>
                  <input
                    type="tel"
                    className="input-field mt-1"
                    placeholder="+521234567890"
                    value={formData.handoff_primary_phone}
                    onChange={e => setFormData(prev => ({ ...prev, handoff_primary_phone: e.target.value }))}
                    onBlur={e => {
                      const v = e.target.value.trim();
                      if (v && !isValidE164(v)) toast.error('Número inválido — usa formato E.164 (+521234567890)');
                    }}
                  />
                  <p className="text-xs mt-1" style={{ color: childPhoneMissing ? '#ef4444' : 'var(--muted)' }}>
                    {childPhoneMissing
                      ? t('agentEditor.childPhoneRequiredHelp', 'Child agents must specify a handoff phone so callers can return to the parent flow.')
                      : t('agentEditor.primaryHandoffPhoneHelp')
                    }
                  </p>
                </div>}

                {/* Based-on (parent agent) — creation mode (hidden in child mode; handled by URL params) */}
                {!isEditing && !isChildMode && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('agentEditor.creationMode')}
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="scratch"
                          checked={creationMode === 'scratch'}
                          onChange={() => {
                            setCreationMode('scratch');
                            setFormData(prev => ({ ...prev, parent_agent_id: '' }));
                          }}
                        />
                        <span className="text-sm">{t('agentEditor.fromScratch')}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="based_on"
                          checked={creationMode === 'based_on'}
                          onChange={() => setCreationMode('based_on')}
                        />
                        <span className="text-sm">{t('agentEditor.basedOnExisting')}</span>
                      </label>
                    </div>
                    {creationMode === 'based_on' && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-foreground mb-1">
                          {t('agentEditor.parentAgent')}
                        </label>
                        <select
                          className="input w-full"
                          value={formData.parent_agent_id}
                          onChange={e => {
                            const parentId = e.target.value;
                            const parent = allAgents.find(a => a.id === parentId);
                            setFormData(prev => ({
                              ...prev,
                              parent_agent_id: parentId,
                              // Clone key fields from parent agent
                              ...(parent ? {
                                voice: parent.voice || prev.voice,
                                language: parent.language || prev.language,
                                tools: parent.tools || prev.tools,
                                tts_provider: parent.tts_provider || prev.tts_provider,
                                elevenlabs_voice_id: parent.elevenlabs_voice_id || prev.elevenlabs_voice_id,
                                elevenlabs_voice_settings: parent.elevenlabs_voice_settings || prev.elevenlabs_voice_settings || {},
                                elevenlabs_model_id: parent.elevenlabs_model_id || prev.elevenlabs_model_id || "",
                                agent_type: parent.agent_type || prev.agent_type,
                                department: parent.department || prev.department,
                              } : {}),
                            }));
                          }}
                        >
                          <option value="">{t('agentEditor.selectParentAgent')}</option>
                          {allAgents.map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Show parent agent link when editing */}
                {isEditing && formData.parent_agent_id && (
                  <div>
                    <p className="text-sm" style={{ color: 'var(--muted)' }}>
                      {t('agentEditor.basedOnExisting')}:{' '}
                      <strong>
                        {allAgents.find(a => a.id === formData.parent_agent_id)?.name || formData.parent_agent_id}
                      </strong>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Allowed Organizations (super_admin only) */}
            {isSuperAdmin && (
              <div className="card">
                <h3 className="text-lg font-medium text-foreground mb-2">{t('agentEditor.allowedOrganizations')}</h3>
                <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>{t('agentEditor.allowedOrganizationsHelp')}</p>
                {tenants.filter(t => t.tenant_id !== user?.tenant_id).length === 0 ? (
                  <p className="text-sm italic" style={{ color: 'var(--muted)' }}>{t('agentEditor.noOtherOrganizations')}</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {tenants
                      .filter(tenant => tenant.tenant_id !== user?.tenant_id)
                      .map(tenant => (
                        <label key={tenant.tenant_id} className="flex items-center space-x-3 p-2 rounded hover:bg-hover cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allowedTenants.includes(tenant.tenant_id)}
                            onChange={() => {
                              setAllowedTenants(prev =>
                                prev.includes(tenant.tenant_id)
                                  ? prev.filter(id => id !== tenant.tenant_id)
                                  : [...prev, tenant.tenant_id]
                              );
                            }}
                            className="h-4 w-4 text-primary-600 focus:ring-primary border-border rounded"
                          />
                          <span className="text-sm text-foreground">{tenant.business_name}</span>
                        </label>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* Preview */}
            <div className="card">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('agentEditor.preview')}</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{t('agentEditor.agentName')}</p>
                  <p className="text-sm text-foreground">{formData.name || t('agentEditor.untitledAgent')}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{t('agentEditor.voiceLabel')}</p>
                  <p className="text-sm text-foreground">
                    {voiceOptions.find(v => v.value === formData.voice)?.label || formData.voice}
                  </p>
                </div>
                {formData.voice_secondary && (
                  <div>
                    <p className="text-sm font-medium text-foreground">{t('agentEditor.secondaryVoice')}</p>
                    <p className="text-sm text-foreground">
                      {voiceOptions.find(v => v.value === formData.voice_secondary)?.label || formData.voice_secondary}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-foreground">{t('agentEditor.languageLabel')}</p>
                  <p className="text-sm text-foreground">
                    {languageOptions.find(l => l.value === formData.language)?.label || formData.language}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{t('agentEditor.availableTools')}</p>
                  <p className="text-sm text-foreground">
                    {formData.tools.length > 0 ? formData.tools.join(', ') : t('common.none')}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{t('agentEditor.voiceSensitivity')}</p>
                  <p className="text-sm text-foreground">
                    {formData.sensitivity_preset === 'responsive' && t('agents.sensitivityResponsive')}
                    {formData.sensitivity_preset === 'balanced' && t('agents.sensitivityBalanced')}
                    {formData.sensitivity_preset === 'conservative' && t('agents.sensitivityConservative')}
                    {formData.sensitivity_preset === 'custom_server' && t('agents.sensitivityCustom')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/agents')}
            className="btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary inline-flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isEditing ? t('agentEditor.updating') : t('agentEditor.creating')}
              </>
            ) : (
              <>
                <CheckIcon className="h-5 w-5 mr-2" />
                {isEditing ? t('agentEditor.updateAgent') : t('agentEditor.createAgentBtn')}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Tool Creation Modal */}
      {showToolModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--overlay)' }}
              onClick={() => setShowToolModal(false)}
            />

            {/* Modal */}
            <div className="inline-block align-bottom bg-card rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="px-6 py-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{editingToolId ? t('agentEditor.editFunction') : t('agentEditor.createFunction')}</h3>
                    <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                      {t('agentEditor.functionModelHelp')}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowToolModal(false)}
                    style={{ color: 'var(--muted)' }}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                {/* AI Generator Input */}
                {showGeneratorInput && (
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <SparklesIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-purple-900 mb-2">{t('agentEditor.aiToolGenerator')}</h4>
                        <p className="text-xs text-purple-700 mb-3">
                          {t('agentEditor.describeToolPrompt')}
                        </p>
                        <textarea
                          value={generatorPrompt}
                          onChange={(e) => setGeneratorPrompt(e.target.value)}
                          placeholder="Example: Create a tool to log payment promises with customer_id, amount, promised_date, and optional notes"
                          rows={2}
                          className="w-full border border-purple-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 placeholder-purple-400"
                          disabled={isGenerating}
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            onClick={generateToolSchema}
                            disabled={isGenerating || !generatorPrompt.trim()}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isGenerating ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                {t('agentEditor.generating')}
                              </>
                            ) : (
                              <>
                                <SparklesIcon className="h-4 w-4 mr-2" />
                                {t('agentEditor.generateSchema')}
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowGeneratorInput(false); setGeneratorPrompt(''); }}
                            className="px-3 py-1.5 text-sm" style={{ color: 'var(--muted)' }}
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Definition Header */}
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-foreground">{t('agentEditor.definition')}</label>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={generateToolSchema}
                      disabled={isGenerating}
                      className="btn-secondary text-sm px-3 py-1.5 inline-flex items-center"
                    >
                      <SparklesIcon className="h-4 w-4 mr-1" />
                      {showGeneratorInput ? t('agentEditor.aiGenerate') : t('agentEditor.generate')}
                    </button>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowExamplesDropdown(!showExamplesDropdown)}
                        className="btn-secondary text-sm px-3 py-1.5 inline-flex items-center"
                      >
                        {t('agentEditor.examples')}
                        <ChevronDownIcon className="ml-1 h-4 w-4" />
                      </button>
                      {showExamplesDropdown && (
                        <div className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-card ring-1 ring-black ring-opacity-5 z-10">
                          <div className="py-1">
                            <button
                              type="button"
                              onClick={() => selectExample('get_weather')}
                              className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-hover"
                            >
                              get_weather
                            </button>
                            <button
                              type="button"
                              onClick={() => selectExample('send_notification')}
                              className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-hover"
                            >
                              send_notification
                            </button>
                            <button
                              type="button"
                              onClick={() => selectExample('lookup_customer')}
                              className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-hover"
                            >
                              lookup_customer
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* JSON Editor */}
                <div className="relative">
                  <textarea
                    value={toolJsonText}
                    onChange={(e) => handleToolJsonChange(e.target.value)}
                    className={`input-field w-full h-72 font-mono text-sm resize-none ${toolJsonError ? 'border-red-500 focus:ring-red-500' : ''}`}
                    style={{ lineHeight: '1.6' }}
                    spellCheck="false"
                  />
                  {toolJsonError && (
                    <p className="mt-2 text-sm text-red-600">
                      {t('agentEditor.jsonError')}: {toolJsonError}
                    </p>
                  )}
                </div>

                {/* Strict mode note */}
                <p className="mt-3 text-xs" style={{ color: 'var(--muted)' }}>
                  {t('agentEditor.strictModeNote')}
                </p>

                {/* Webhook URL */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('agentEditor.webhookUrlOptional')}
                  </label>
                  <textarea
                    rows={2}
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="https://api.example.com/webhook"
                    className="input-field w-full font-mono resize-y overflow-y-auto"
                  />
                  <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
                    {t('agentEditor.webhookUrlHelp')}
                  </p>
                  <p className="mt-1 text-xs text-blue-600">
                    {t('agentEditor.webhookUrlEmpty')}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-background border-t border-border flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowToolModal(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleCreateTool}
                  disabled={creatingTool || !!toolJsonError}
                  className="btn-primary inline-flex items-center"
                >
                  {creatingTool ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingToolId ? t('agentEditor.updating') : t('agentEditor.adding')}
                    </>
                  ) : (
                    <>
                      {editingToolId ? <CheckIcon className="h-4 w-4 mr-1" /> : <PlusIcon className="h-4 w-4 mr-1" />}
                      {editingToolId ? t('agentEditor.updateFunction') : t('agentEditor.addFunction')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentEditor;